import { Box, Button, IconButton, Modal, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useState } from "react";

type GuideType = "main" | "report";

// localStorage 키는 가이드 종류별로 분리
export const GUIDE_KEYS: Record<GuideType, string> = {
  main: "projectGuideSeen",
  report: "reportGuideSeen",
};

export const isGuideSeen = (type: GuideType) => {
  try {
    return !!localStorage.getItem(GUIDE_KEYS[type]);
  } catch {
    return true;
  }
};

// 이미지는 public/ 기준 경로. 내용 바꾸려면 이 배열만 수정
// 캡처: playwright, 1600x900 (/main/project/:id 기준)
const MAIN_SLIDES = [
  {
    image: "/manual/guide/guide_server.png",
    title: "AI 서버 선택",
    text: "프로젝트에 들어오면 학습에 사용할 AI 서버를 먼저 선택해요. 서버 없이 라벨링만 하려면 '서버 선택없이 진행하기'를 누르세요.",
  },
  {
    image: "/manual/guide/guide_main.png",
    title: "작업 화면 구성",
    text: "왼쪽은 이미지 목록(라벨·AI 라벨·스코어), 가운데는 이미지 뷰어, 오른쪽은 AI 옵션과 검사 결과 패널이에요.",
  },
  {
    image: "/manual/guide/guide_keymap.png",
    title: "단축키로 빠른 라벨링",
    text: "상단 OK/NG 버튼이나 숫자 키로 바로 라벨링할 수 있어요. 키보드 아이콘에서 단축키를 원하는 대로 바꿀 수 있어요.",
  },
  {
    image: "/manual/guide/guide_filter.png",
    title: "필터 · 일괄 라벨링 · 내보내기",
    text: "라벨과 AI 결과가 다른 이미지만 모아 보고, Shift/Ctrl+클릭으로 여러 장을 한 번에 라벨링하거나 CSV로 내보낼 수 있어요.",
  },
  {
    image: "/manual/guide/guide_image_filter.png",
    title: "이미지 필터",
    text: "밝기·대비·채도·흐림을 조절하거나 OpenCV 필터를 적용해 결함을 더 잘 보이게 할 수 있어요.",
  },
  {
    image: "/manual/guide/guide_analysis.png",
    title: "클래스별 분석",
    text: "분석 화면에서 클래스별로 분류된 이미지를 한눈에 보고, 학습 실행 버튼으로 바로 학습을 시작할 수 있어요.",
  },
  {
    image: "/manual/guide/guide_label.png",
    title: "클래스 상세 작업",
    text: "각 클래스의 DETAIL을 누르면 해당 클래스 이미지만 모아서 확인하고 라벨을 수정할 수 있어요.",
  },
];

// 캡처: /main/project/:id/report 기준
const REPORT_SLIDES = [
  {
    image: "/manual/guide/guide_report_new.png",
    title: "빈 리포트 생성하기",
    text: "'새 리포트 만들기'에서 빈 리포트를 선택하면 아무 항목 없이 시작해서 원하는 내용만 직접 구성할 수 있어요.",
  },
  {
    image: "/manual/guide/guide_report_template.png",
    title: "템플릿 선택해서 확인하기",
    text: "템플릿 선택에서 미리 만들어진 템플릿을 고르면 미리보기로 구성을 확인한 뒤 바로 리포트로 사용할 수 있어요.",
  },
  {
    image: "/manual/guide/guide_report_add.png",
    title: "항목 추가하기",
    text: "Detail 화면에서 '추가'를 눌러 차트·표 등 필요한 항목을 리포트에 더하고, 배치와 크기를 조절할 수 있어요.",
  },
  {
    image: "/manual/guide/guide_report_setting.png",
    title: "리포트 설정",
    text: "Setting 메뉴에서 대상 클래스, 기간, 색상 테마 등 리포트 전반의 설정을 바꿀 수 있어요.",
  },
];

const SLIDES: Record<GuideType, typeof MAIN_SLIDES> = { main: MAIN_SLIDES, report: REPORT_SLIDES };

export default function WelcomeGuideModal({
  open,
  onClose,
  type = "main",
}: {
  open: boolean;
  onClose: () => void;
  type?: GuideType;
}) {
  const [page, setPage] = useState(0);
  const slides = SLIDES[type];
  const slide = slides[page];
  const isLast = page === slides.length - 1;

  const handleClose = () => {
    try {
      localStorage.setItem(GUIDE_KEYS[type], "true");
    } catch {}
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(860px, 92vw)",
          bgcolor: "background.paper",
          borderRadius: 3,
          boxShadow: 24,
          p: 3,
          outline: "none",
        }}
      >
        <IconButton onClick={handleClose} sx={{ position: "absolute", top: 8, right: 8 }}>
          <X size={20} />
        </IconButton>

        <Typography variant="h6" fontWeight={700} mb={2}>
          {slide.title}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton disabled={page === 0} onClick={() => setPage(page - 1)}>
            <ChevronLeft />
          </IconButton>
          <Box
            component="img"
            src={slide.image}
            alt={slide.title}
            sx={{ flex: 1, minWidth: 0, maxHeight: "55vh", objectFit: "contain", borderRadius: 2, bgcolor: "#F5F6FA" }}
          />
          <IconButton disabled={isLast} onClick={() => setPage(page + 1)}>
            <ChevronRight />
          </IconButton>
        </Box>

        <Typography mt={2} color="text.secondary">
          {slide.text}
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {page + 1} / {slides.length}
          </Typography>
          <Button variant="contained" onClick={isLast ? handleClose : () => setPage(page + 1)}>
            {isLast ? "시작하기" : "다음"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
